"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";

// Dynamically import the Framer HomeLogin component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HomeLogin = dynamic<any>(
  () => import("@/framer/home-login").then((mod) => mod.default),
  { ssr: false }
);

// Dynamically import the standalone CheckBox component
const CheckBox = dynamic(
  () => import("@/framer/check-box").then((mod) => mod.default),
  { ssr: false }
);

interface RememberedAccount {
  userId: string;
  displayName: string | null;
  email: string | null;
  avatarUrl: string | null;
  hasPasskey: boolean;
  hasBiometric: boolean;
  lastUsedAt: string | null;
}

export type AuthVariant = 'login' | 'signup';

interface HomeLoginInteractiveProps {
  email: string;
  password: string;
  confirmPassword?: string;
  variant?: AuthVariant;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onConfirmPasswordChange?: (confirmPassword: string) => void;
  onVariantChange?: (variant: AuthVariant) => void;
  onSubmit?: () => void;
  /** Called when the Save Login checkbox state changes */
  onSaveLoginChange?: (enabled: boolean) => void;
  /** Initial state of the Save Login checkbox (default: true - checked) */
  initialSaveLogin?: boolean;
  /** Error message to display (shows 'Invalid Credentials' text when set) */
  error?: string;
  /** Device fingerprint for fetching remembered accounts */
  deviceFingerprint?: string;
  /** Called when an account is selected from the Switch Accounts overlay */
  onAccountSelect?: (account: RememberedAccount) => void;
  /** Skip the 5-second appear animation (used by LockScreenModal) */
  skipAppearAnimation?: boolean;
  /** Focus target used when the wrapper is mounted inside a blocking auth dialog. */
  initialFocusField?: 'email' | 'password';
  className?: string;
  style?: React.CSSProperties;
}

// Framer variant IDs for the CheckBox component
const CHECKBOX_CHECKED_VARIANT = "yjZ6SxCnc";
const CHECKBOX_UNCHECKED_VARIANT = "FLAo4tUYB";

const STRINGS = {
  emailInputLabel: "Email address",
  passwordInputLabel: "Password",
  confirmPasswordInputLabel: "Confirm password",
} as const;

/**
 * Interactive wrapper for the Framer HomeLogin component.
 * Replaces static email and password text with functional input fields.
 * Integrates with the Framer visibility toggle for password show/hide.
 * Monitors the Save Login checkbox for state changes.
 */
export function HomeLoginInteractive({
  email,
  password,
  confirmPassword = '',
  variant = 'login',
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onVariantChange,
  onSubmit,
  onSaveLoginChange,
  initialSaveLogin = true,
  error,
  deviceFingerprint,
  onAccountSelect,
  skipAppearAnimation,
  initialFocusField,
  className,
  style,
}: HomeLoginInteractiveProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isSaveLoginEnabled, setIsSaveLoginEnabled] = useState(initialSaveLogin);
  const saveLoginStateRef = useRef(initialSaveLogin); // Ref to track current state in event handlers
  const [checkboxPortalTarget, setCheckboxPortalTarget] = useState<HTMLElement | null>(null);
  
  // Switch Accounts overlay state
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [rememberedAccounts, setRememberedAccounts] = useState<RememberedAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const switchAccountButtonRef = useRef<HTMLElement | null>(null);
  
  // Ref to track latest onSubmit callback (avoids stale closure issues)
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  // Keep ref in sync with state
  useEffect(() => {
    saveLoginStateRef.current = isSaveLoginEnabled;
  }, [isSaveLoginEnabled]);

  // Inject email input
  const injectEmailInput = useCallback(() => {
    if (!containerRef.current) return;

    const usernameTextEl = containerRef.current.querySelector(
      '[data-framer-name="UsernameText"]'
    ) as HTMLElement;

    if (!usernameTextEl) return;
    if (usernameTextEl.querySelector('input[data-email-input]')) return;

    const parentContainer = usernameTextEl.closest('.framer-d7qnmz') as HTMLElement;
    if (parentContainer) {
      parentContainer.style.position = 'relative';
    }

    const richTextWrapper = usernameTextEl.querySelector('.framer-text') as HTMLElement;
    if (richTextWrapper) {
      richTextWrapper.style.visibility = 'hidden';
    }

    const input = document.createElement('input');
    input.type = 'email';
    input.name = 'email';
    input.id = 'login-email';
    input.setAttribute('aria-label', STRINGS.emailInputLabel);
    input.placeholder = 'user@example.com';
    input.dataset.emailInput = 'true';
    input.value = email;
    input.autocomplete = 'email';

    Object.assign(input.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: '300',
      fontStyle: 'italic',
      color: '#5F6C7B',
      textAlign: 'right',
      padding: '0',
      margin: '0',
    });

    input.addEventListener('focus', () => {
      input.style.fontWeight = '400';
      input.style.fontStyle = 'normal';
      input.style.color = '#2e2e2e';
    });

    input.addEventListener('blur', () => {
      if (!input.value) {
        input.style.fontWeight = '300';
        input.style.fontStyle = 'italic';
        input.style.color = '#5F6C7B';
      }
    });

    input.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      onEmailChange(target.value);
      if (target.value) {
        input.style.fontWeight = '400';
        input.style.fontStyle = 'normal';
        input.style.color = '#2e2e2e';
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        passwordInputRef.current?.focus();
      }
    });

    usernameTextEl.style.position = 'relative';
    usernameTextEl.appendChild(input);
    emailInputRef.current = input;
  }, [email, onEmailChange]);

  // Inject password input
  const injectPasswordInput = useCallback(() => {
    if (!containerRef.current) return;

    const passwordTextEl = containerRef.current.querySelector(
      '[data-framer-name="Password"]'
    ) as HTMLElement;

    if (!passwordTextEl) return;
    if (passwordTextEl.querySelector('input[data-password-input]')) return;

    const parentContainer = passwordTextEl.closest('.framer-dfv9wm') as HTMLElement;
    if (parentContainer) {
      parentContainer.style.position = 'relative';
    }

    const richTextWrapper = passwordTextEl.querySelector('.framer-text') as HTMLElement;
    if (richTextWrapper) {
      richTextWrapper.style.visibility = 'hidden';
    }

    const input = document.createElement('input');
    input.type = 'password';
    input.name = 'password';
    input.id = 'login-password';
    input.setAttribute('aria-label', STRINGS.passwordInputLabel);
    input.placeholder = '••••••••';
    input.dataset.passwordInput = 'true';
    input.value = password;
    input.autocomplete = 'current-password';

    Object.assign(input.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: '400',
      color: '#2e2e2e',
      textAlign: 'right',
      padding: '0',
      margin: '0',
      letterSpacing: '2px',
    });

    input.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      onPasswordChange(target.value);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // If signup variant, focus confirm password field instead of submitting
        if (variant === 'signup' && confirmPasswordInputRef.current) {
          confirmPasswordInputRef.current.focus();
        } else {
          onSubmitRef.current?.();
        }
      }
    });

    passwordTextEl.style.position = 'relative';
    passwordTextEl.appendChild(input);
    passwordInputRef.current = input;
  }, [password, onPasswordChange, variant]); // Use variant prop

  // Inject confirm password input (for signup variant only)
  const injectConfirmPasswordInput = useCallback(() => {
    if (!containerRef.current) return;

    // Find all PasswordStack elements - the second one is the confirm password in signup variant
    const allPasswordStacks = containerRef.current.querySelectorAll(
      '[data-framer-name="PasswordStack"]'
    ) as NodeListOf<HTMLElement>;
    
    // The confirm password is the second PasswordStack (in signup variant)
    const confirmPasswordStack = allPasswordStacks.length > 1 ? allPasswordStacks[1] : null;
    
    if (!confirmPasswordStack) return;
    
    // Find the Password text element within this stack
    const confirmPasswordEl = confirmPasswordStack.querySelector('[data-framer-name="Password"]') as HTMLElement;
    if (!confirmPasswordEl) return;
    if (confirmPasswordEl.querySelector('input[data-confirm-password-input]')) return;

    const parentContainer = confirmPasswordEl.closest('.framer-yimz2t') as HTMLElement;
    if (parentContainer) {
      parentContainer.style.position = 'relative';
    }

    // Change the label from "Password:" to "Confirm:" for the confirm password field
    const labelEl = confirmPasswordStack.querySelector('[data-framer-name="PasswordLabel"]') as HTMLElement;
    if (labelEl) {
      const pEl = labelEl.querySelector('p') as HTMLElement;
      if (pEl && pEl.textContent?.includes('Password')) {
        pEl.textContent = 'Confirm: ';
      }
    }

    const richTextWrapper = confirmPasswordEl.querySelector('.framer-text') as HTMLElement;
    if (richTextWrapper) {
      richTextWrapper.style.visibility = 'hidden';
    }

    const input = document.createElement('input');
    input.type = 'password';
    input.name = 'confirmPassword';
    input.id = 'signup-confirm-password';
    input.setAttribute('aria-label', STRINGS.confirmPasswordInputLabel);
    input.placeholder = '••••••••';
    input.dataset.confirmPasswordInput = 'true';
    input.value = confirmPassword;
    input.autocomplete = 'new-password';

    Object.assign(input.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: '400',
      color: '#2e2e2e',
      textAlign: 'right',
      padding: '0',
      margin: '0',
      letterSpacing: '2px',
    });

    input.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      onConfirmPasswordChange?.(target.value);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmitRef.current?.();
      }
    });

    confirmPasswordEl.style.position = 'relative';
    confirmPasswordEl.appendChild(input);
    confirmPasswordInputRef.current = input;
  }, [confirmPassword, onConfirmPasswordChange]);

  // Set up visibility toggle listener
  const setupVisibilityToggle = useCallback(() => {
    if (!containerRef.current) return;

    // Find the visibility toggle component (data-framer-name="Visible" or "Hidden")
    const visibilityToggle = containerRef.current.querySelector(
      '[data-framer-name="Visible"], [data-framer-name="Hidden"]'
    ) as HTMLElement;

    if (!visibilityToggle) return;
    if (visibilityToggle.dataset.visibilityListenerAttached) return;

    visibilityToggle.dataset.visibilityListenerAttached = 'true';

    // Use MutationObserver to watch for variant changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-framer-name') {
          const target = mutation.target as HTMLElement;
          const isHidden = target.getAttribute('data-framer-name') === 'Hidden';
          setIsPasswordVisible(!isHidden);
        }
      });
    });

    observer.observe(visibilityToggle, {
      attributes: true,
      attributeFilter: ['data-framer-name'],
    });

    // Check initial state
    const isHidden = visibilityToggle.getAttribute('data-framer-name') === 'Hidden';
    setIsPasswordVisible(!isHidden);

    return () => observer.disconnect();
  }, []);

  // Handle checkbox toggle
  const handleCheckboxClick = useCallback(() => {
    const newState = !saveLoginStateRef.current;
    saveLoginStateRef.current = newState;
    setIsSaveLoginEnabled(newState);
    onSaveLoginChange?.(newState);
  }, [onSaveLoginChange]);

  // Set up Save Login checkbox by finding the container and replacing with our React CheckBox
  const setupSaveLoginToggle = useCallback(() => {
    if (!containerRef.current) return;

    // Find the SaveLogin container
    const saveLoginContainer = containerRef.current.querySelector(
      '[data-framer-name="SaveLogin"]'
    ) as HTMLElement;

    if (!saveLoginContainer) return;

    // Find the CheckBox component container (the framer-1nb3tsb-container)
    const checkboxContainer = saveLoginContainer.querySelector(
      '.framer-1nb3tsb-container'
    ) as HTMLElement;

    if (!checkboxContainer) return;
    if (checkboxContainer.dataset.customCheckboxInjected) return;

    checkboxContainer.dataset.customCheckboxInjected = 'true';

    // Hide the original Framer checkbox completely
    const originalCheckbox = checkboxContainer.querySelector('.framer-1oupo8i') as HTMLElement;
    if (originalCheckbox) {
      originalCheckbox.style.display = 'none';
    }

    // Make checkbox container relative for absolute positioning
    checkboxContainer.style.position = 'relative';
    
    // Create a portal target div for our React CheckBox
    const portalTarget = document.createElement('div');
    portalTarget.dataset.checkboxPortal = 'true';
    Object.assign(portalTarget.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '16px',
      height: '16px',
      zIndex: '10',
    });
    checkboxContainer.appendChild(portalTarget);
    setCheckboxPortalTarget(portalTarget);

    // Also make the SaveLogin text clickable
    const saveLoginText = saveLoginContainer.querySelector('[data-framer-name="SaveLoginText"]') as HTMLElement;
    if (saveLoginText && !saveLoginText.dataset.clickListenerAttached) {
      saveLoginText.dataset.clickListenerAttached = 'true';
      saveLoginText.style.cursor = 'pointer';
      saveLoginText.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Toggle using the current ref value
        const newState = !saveLoginStateRef.current;
        saveLoginStateRef.current = newState;
        setIsSaveLoginEnabled(newState);
        onSaveLoginChange?.(newState);
      });
    }
  }, [onSaveLoginChange]);

  // Update password input type based on visibility state
  useEffect(() => {
    if (passwordInputRef.current) {
      passwordInputRef.current.type = isPasswordVisible ? 'text' : 'password';
    }
  }, [isPasswordVisible]);

  // Set up Login button click handler
  const setupLoginButton = useCallback(() => {
    if (!containerRef.current) return;

    // Find the Login button (AutoMessageSaveButton)
    const loginButton = containerRef.current.querySelector(
      '[data-framer-name="AutoMessageSaveButton"]'
    ) as HTMLElement;

    if (!loginButton) return;
    if (loginButton.dataset.loginListenerAttached) return;

    loginButton.dataset.loginListenerAttached = 'true';
    loginButton.style.cursor = 'pointer';

    loginButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('[HomeLoginInteractive] Login button clicked');
      // Use ref to get the latest onSubmit callback
      onSubmitRef.current?.();
    });
  }, []); // No dependencies - we use the ref

  // Fetch remembered accounts when overlay opens
  const fetchRememberedAccounts = useCallback(async () => {
    if (!deviceFingerprint) return;
    
    setLoadingAccounts(true);
    try {
      const response = await fetch("/api/auth/remembered-accounts", {
        headers: {
          "x-device-fingerprint": deviceFingerprint,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRememberedAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error("Failed to fetch remembered accounts:", err);
    } finally {
      setLoadingAccounts(false);
    }
  }, [deviceFingerprint]);

  // Toggle Switch Accounts overlay
  const toggleAccountSwitcher = useCallback(() => {
    setShowAccountSwitcher(prev => {
      const newState = !prev;
      if (newState) {
        fetchRememberedAccounts();
      }
      return newState;
    });
  }, [fetchRememberedAccounts]);

  // Handle account selection
  const handleAccountClick = useCallback((account: RememberedAccount) => {
    setShowAccountSwitcher(false);
    onAccountSelect?.(account);
  }, [onAccountSelect]);

  // Close overlay when clicking outside
  useEffect(() => {
    if (!showAccountSwitcher) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const overlay = document.querySelector('[data-account-switcher-overlay]');
      const button = switchAccountButtonRef.current;
      
      if (overlay && !overlay.contains(e.target as Node) && 
          button && !button.contains(e.target as Node)) {
        setShowAccountSwitcher(false);
      }
    };
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowAccountSwitcher(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showAccountSwitcher]);

  // Set up Switch Accounts button click handler
  const setupSwitchAccountsButton = useCallback(() => {
    if (!containerRef.current) return;

    // Find the SwitchAccount button
    const switchAccountButton = containerRef.current.querySelector(
      '[data-framer-name="SwitchAccount"]'
    ) as HTMLElement;

    if (!switchAccountButton) return;
    if (switchAccountButton.dataset.switchAccountListenerAttached) return;

    switchAccountButton.dataset.switchAccountListenerAttached = 'true';
    switchAccountButton.style.cursor = 'pointer';
    switchAccountButtonRef.current = switchAccountButton;

    switchAccountButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleAccountSwitcher();
    });
  }, [toggleAccountSwitcher]);

  // Set up variant toggle click handlers (Sign Up / Log In links)
  const setupVariantToggle = useCallback(() => {
    if (!containerRef.current) return;

    // Find all elements that can toggle variant - typically "Sign Up" and "Log In" links
    // In Login variant: "Sign Up" link switches to signup
    // In Signup variant: "Log In" link switches to login
    
    // Find Sign Up link in login view (text that says "Sign Up")
    const signUpLinks = containerRef.current.querySelectorAll('p, span');
    signUpLinks.forEach((el) => {
      const text = el.textContent?.trim();
      if (text === 'Sign Up' && !el.closest('[data-framer-name="SignUpHeader"]')) {
        const element = el as HTMLElement;
        if (element.dataset.variantToggleAttached) return;
        element.dataset.variantToggleAttached = 'true';
        element.style.cursor = 'pointer';
        element.addEventListener('click', (e) => {
          // Only handle if this is a navigation link, not the header
          if (!element.closest('[data-framer-name="AutoMessageSaveButton"]')) {
            e.preventDefault();
            e.stopPropagation();
            onVariantChange?.('signup');
          }
        });
      }
      if (text === 'Log In' && !el.closest('[data-framer-name="AutoMessageSaveButton"]')) {
        const element = el as HTMLElement;
        if (element.dataset.variantToggleAttached) return;
        element.dataset.variantToggleAttached = 'true';
        element.style.cursor = 'pointer';
        element.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          onVariantChange?.('login');
        });
      }
    });
  }, [onVariantChange]);

  // Hide debug/config elements (no longer needed since LockScreenModal skips login page)
  // Keeping as a safety net for any stray Configuration elements
  const hideDebugElements = useCallback(() => {
    if (!containerRef.current) return;
    
    // Hide Configuration text and any other debug elements
    const configElements = containerRef.current.querySelectorAll('p, span');
    
    configElements.forEach((el) => {
      const text = el.textContent?.trim();
      if (text === 'Configuration') {
        const element = el as HTMLElement;
        element.style.display = 'none';
        
        // Also hide parent if it only contains this text
        const parent = element.parentElement;
        if (parent && parent.textContent?.trim() === 'Configuration') {
          parent.style.display = 'none';
        }
      }
    });
  }, []);

  // Main effect to inject inputs when component mounts
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new MutationObserver(() => {
      const usernameTextEl = containerRef.current?.querySelector(
        '[data-framer-name="UsernameText"]'
      );
      const passwordTextEl = containerRef.current?.querySelector(
        '[data-framer-name="Password"]'
      );
      const saveLoginEl = containerRef.current?.querySelector(
        '[data-framer-name="SaveLogin"]'
      );
      const switchAccountEl = containerRef.current?.querySelector(
        '[data-framer-name="SwitchAccount"]'
      );
      
      if (usernameTextEl) {
        injectEmailInput();
      }
      if (passwordTextEl) {
        injectPasswordInput();
        // Also inject confirm password for signup variant
        injectConfirmPasswordInput();
        setupVisibilityToggle();
      }
      if (saveLoginEl) {
        setupSaveLoginToggle();
      }
      if (switchAccountEl) {
        setupSwitchAccountsButton();
      }
      // Always try to set up the login button and variant toggles
      setupLoginButton();
      setupVariantToggle();
      
      if (usernameTextEl && passwordTextEl) {
        setIsReady(true);
      }
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    // Try immediately
    injectEmailInput();
    injectPasswordInput();
    injectConfirmPasswordInput();
    setupVisibilityToggle();
    setupSaveLoginToggle();
    setupLoginButton();
    setupSwitchAccountsButton();
    setupVariantToggle();
    hideDebugElements();

    return () => observer.disconnect();
  }, [injectEmailInput, injectPasswordInput, injectConfirmPasswordInput, setupVisibilityToggle, setupSaveLoginToggle, setupLoginButton, setupSwitchAccountsButton, setupVariantToggle, hideDebugElements]);

  useEffect(() => {
    if (!initialFocusField) return undefined;

    let attempts = 0;
    let timer: number | undefined;
    const focusInitialField = () => {
      const target = initialFocusField === 'password'
        ? passwordInputRef.current
        : emailInputRef.current;

      const targetStyle = target ? window.getComputedStyle(target) : null;
      const targetIsVisible = Boolean(
        target?.isConnected
        && target.getClientRects().length > 0
        && targetStyle?.display !== 'none'
        && targetStyle?.visibility !== 'hidden',
      );

      if (targetIsVisible && target) {
        target.focus({ preventScroll: true });
        if (document.activeElement === target) return;
      }

      attempts += 1;
      if (attempts < 300) timer = window.setTimeout(focusInitialField, 50);
    };

    focusInitialField();

    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [initialFocusField]);

  // Sync email value
  useEffect(() => {
    if (emailInputRef.current && emailInputRef.current.value !== email) {
      emailInputRef.current.value = email;
      if (email) {
        emailInputRef.current.style.fontWeight = '400';
        emailInputRef.current.style.fontStyle = 'normal';
        emailInputRef.current.style.color = '#2e2e2e';
      } else {
        emailInputRef.current.style.fontWeight = '300';
        emailInputRef.current.style.fontStyle = 'italic';
        emailInputRef.current.style.color = '#5F6C7B';
      }
    }
  }, [email]);

  // Sync password value
  useEffect(() => {
    if (passwordInputRef.current && passwordInputRef.current.value !== password) {
      passwordInputRef.current.value = password;
    }
  }, [password]);

  // Sync confirm password value
  useEffect(() => {
    if (confirmPasswordInputRef.current && confirmPasswordInputRef.current.value !== confirmPassword) {
      confirmPasswordInputRef.current.value = confirmPassword;
    }
  }, [confirmPassword]);

  // Update error text content via DOM since Framer's invalidText prop doesn't override children
  useEffect(() => {
    if (!containerRef.current || !error) return;
    
    // Find all error text elements and update their content
    const errorContainers = containerRef.current.querySelectorAll('[data-framer-name="Error"]');
    errorContainers.forEach((container) => {
      const textEl = container.querySelector('p');
      if (textEl) {
        textEl.textContent = error;
      }
    });
  }, [error]);

  // Error visibility is controlled via CSS (.show-error class)
  // invalidCredentials/invalidSignup props control Framer's internal rendering

  return (
    <div 
      ref={containerRef} 
      className={`${className || ''} ${error ? 'show-error' : ''}`} 
      style={{ ...style, position: 'relative' }}
    >
      <HomeLogin 
        variant={variant === 'signup' ? 'Signup' : 'Login'}
        invalidCredentials={error ? true : false}
        invalidSignup={error ? true : false}
        invalidText={error || 'Invalid Credentials'}
        skipAppearAnimation={skipAppearAnimation}
      />
      {/* Render the controlled CheckBox via portal into the Framer component */}
      {checkboxPortalTarget && createPortal(
        <CheckBox
          variant={isSaveLoginEnabled ? "Variant 1" : "Variant 2"}
          click={handleCheckboxClick}
          width={16}
          height={16}
        />,
        checkboxPortalTarget
      )}
      
      {/* Switch Accounts Overlay - ProfileSettings style */}
      {showAccountSwitcher && (
        <div
          data-account-switcher-overlay
          style={{
            position: 'absolute',
            top: 200, // Position below the Switch Accounts button area
            left: '50%',
            transform: 'translateX(-50%)',
            width: 320,
            backgroundColor: 'white',
            borderRadius: 16,
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
            padding: 16,
            zIndex: 1000,
          }}
        >
          {/* Overlay content */}
          {loadingAccounts ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}>
              <div style={{
                width: 24,
                height: 24,
                border: '2px solid #5ba9e1',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
            </div>
          ) : rememberedAccounts.length === 0 ? (
            /* Empty state */
            <div style={{
              padding: '16px 8px',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                fontWeight: 400,
                fontStyle: 'italic',
                color: '#5F6C7B',
                margin: 0,
              }}>
                No saved Logins...
              </p>
            </div>
          ) : (
            /* Account list */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rememberedAccounts.map((account) => (
                <button
                  key={account.userId}
                  onClick={() => handleAccountClick(account)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 12,
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#e8f4fc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}>
                    {account.avatarUrl ? (
                      <img 
                        src={account.avatarUrl} 
                        alt="" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 16,
                        fontWeight: 500,
                        color: '#5ba9e1',
                      }}>
                        {(account.displayName || account.email || '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  {/* Account info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#2e2e2e',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {account.displayName || account.email || 'Unknown User'}
                    </p>
                    {account.displayName && account.email && (
                      <p style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#5F6C7B',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {account.email}
                      </p>
                    )}
                  </div>
                  
                  {/* Biometric/Passkey indicator */}
                  {(account.hasBiometric || account.hasPasskey) && (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5ba9e1"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HomeLoginInteractive;
